import { IDispatchResponse, SiteManualStatus } from "../types/asset/status-hive";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { getApiAccessToken } from "../auth/helpers";
import { IPNPlanSubmission } from "../types/dispatch/IFailedPnMetrics";
import { IPerfmonFilePDF, IPerfmonFileSubmission } from "../types/dispatch/IFailedPerformanceMetrics";
import { IDispatchAlertSubscriber, IDispatchAlertSubscribers } from "../types/dispatch/IDispatchAlertSubscribers";

const manualStartUrl = "https://pi-application.conradcloud.net/iONManualControl/GasSiteManualStart"

function makeRequest(method: string, url: string) {
    return new Promise(function (resolve, reject) {
        let xhr = new XMLHttpRequest();
        xhr.open(method, url);
        xhr.withCredentials = true;
        xhr.setRequestHeader('Content-Type', 'text/plain')
        if (method.toLowerCase() === "post") {
            xhr.setRequestHeader('Content-Type', 'application/json');
        }

        xhr.onload = function () {
            if (this.status >= 200 && this.status < 300) {
                resolve(xhr.response);
            } else {
                reject({
                    status: this.status,
                    statusText: xhr.statusText
                });
            }
        };
        xhr.onerror = function () {
            reject({
                status: this.status,
                statusText: xhr.statusText
            });
        };
        xhr.send(null);
    });
}

const sendManualDispatchRequest = async (siteName: string, webID: string, newStatus: SiteManualStatus) => {
    let responseStatus = true

    try {
        const path = "\\\\PI-AF\\iON\\Conrad\\" + siteName + "|Site Manual Control";
        let payloadValue = 0;
        if (newStatus === SiteManualStatus.On) { payloadValue = 2 }
        else if (newStatus === SiteManualStatus.Off) { payloadValue = 4 }
        const queryString = "?WebID=" + webID + "&ManualAction=" + payloadValue + "&Site=" + path;
        const fullUrl = manualStartUrl + queryString;
        await makeRequest("POST", fullUrl);
    }
    catch {
        responseStatus = false
    }
    return {
        siteName: siteName,
        status: responseStatus
    } as IDispatchResponse
}


const piApplicationDispatchApi = {
    sendManualDispatchRequest
}

export default piApplicationDispatchApi;

export const dispatchApi = createApi({
    reducerPath: 'dispatchApi',
    baseQuery: fetchBaseQuery({
        baseUrl: `${process.env.REACT_APP_PORTAL_API_URL}dispatch`,
        prepareHeaders: async (headers: Headers, { getState }) => {
            const accessToken = await getApiAccessToken();
            if (accessToken) {
                headers.set("Authorization", `Bearer ${accessToken}`);
            }
        }
    }),
    tagTypes: ["PNPlans", "PerfmonFiles", "AlertSubscribers"],
    endpoints: (builder) => ({
        getPNPlanSubmissions: builder.query<IPNPlanSubmission[], void>({
            query: () => '/pnPlanSubmissions',
            keepUnusedDataFor: 10 * 60,
            providesTags: ["PNPlans"]
        }),
        resubmitPNPlan: builder.mutation<void, IPNPlanSubmission>({
            query: (payload) => ({
                url: '/resubmitPNPlan',
                method: 'POST',
                body: payload
            })
        }),
        getPerfmonFileSubmissions: builder.query<IPerfmonFileSubmission[], void>({
            query: () => '/perfmonFileSubmissions',
            keepUnusedDataFor: 10 * 60,
            providesTags: ["PerfmonFiles"]
        }),
        downloadPerfmonFile: builder.query<IPerfmonFilePDF, { id: string }>({
            query: (payload) => ({
                url: `/downloadPerfmonFile`,
                method: 'POST',
                body: payload,
                responseHandler: async (response) => {
                    const buffer = await response.arrayBuffer();
                    const blob = new Blob([buffer], { type: "application/csv" });
                    const invoicePdf: IPerfmonFilePDF = {
                        url: `${URL.createObjectURL(blob)}`,
                        isValid: blob.size > 0,
                    }
                    return invoicePdf;
                },
            }),
        }),
        uploadPerfmonFile: builder.mutation<void, { siteID: string, fileStartTime: string, file: File }>({
            query: (payload) => {
                const formData = new FormData();
                formData.append("siteID", payload.siteID);
                formData.append("fileStartTime", payload.fileStartTime);
                formData.append("File", payload.file);
                return {
                    url: `uploadPerfmonFile`,
                    method: 'POST',
                    body: formData
                }
            }
        }),
        getDispatchAlertSubscribers: builder.query<IDispatchAlertSubscribers, void>({
            query: () => '/dispatchAlertSubscribers',
            keepUnusedDataFor: 10 * 60,
            providesTags: ["AlertSubscribers"]
        }),
        addDispatchAlertSubscriber: builder.mutation<void, IDispatchAlertSubscriber>({
            query: (payload) => ({
                url: '/dispatchAlertSubscribers',
                method: 'POST',
                body: payload
            }),
            invalidatesTags: ["AlertSubscribers"]
        }),
        removeDispatchAlertSubscriber: builder.mutation<void, IDispatchAlertSubscriber>({
            query: (payload) => ({
                url: '/dispatchAlertSubscribers',
                method: 'DELETE',
                body: payload
            }),
            invalidatesTags: ["AlertSubscribers"]
        }),
    }),
})

export const { useGetPNPlanSubmissionsQuery, useResubmitPNPlanMutation, useGetPerfmonFileSubmissionsQuery, useLazyDownloadPerfmonFileQuery,
    useUploadPerfmonFileMutation, useGetDispatchAlertSubscribersQuery, useAddDispatchAlertSubscriberMutation, useRemoveDispatchAlertSubscriberMutation } = dispatchApi